/** @jsx tsx */
import { tsx } from "@dojo/framework/core/vdom";
import { DNode } from "@dojo/framework/core/interfaces";
import { WidgetBase } from "@dojo/framework/core/WidgetBase";
import { LineCap } from "../interfaces/model/enumerations/index";
import { ILineProperties } from "../interfaces/model/properties/index";
import { Marker } from "./Marker";

/** Value of the marker width if none is correctly provided. */
const DEFAULT_MARKER_WIDTH = 2;

/**
 * Marker object render interface
 */
export interface IMarkerObjectRenderProps {
    lineProps: ILineProperties;
    markerHeadID: string;
    markerTailID: string;
    markerWidth?: number;
    markerHeight?: number;
    refX?: number;
    refY?: number;
}

/**
 * Marker object renderer
 */
export class MarkerObject extends WidgetBase<IMarkerObjectRenderProps> {
	/**
     * Override WidgetBase#render to produce a virtual DOM tree.
     * @returns {HNode} Each time render() executes, it should build the entire virtual DOM tree.
     */
	protected render() {
		const { headEnd, tailEnd } = this.properties.lineProps;
		const { markerHeadID, markerTailID, lineProps, refX, refY } = this.properties;
		const markerObject: DNode [] = [];
		const MARKER_WIDTH = {
			1: 10,
			2: 6,
			3: 5,
			4: 4.5,
			5: 4,
		} as {[key: number]: number};
		const markerDimension = MARKER_WIDTH[lineProps.W || DEFAULT_MARKER_WIDTH] || MARKER_WIDTH[DEFAULT_MARKER_WIDTH];
		const headMarkerProperties = {
			key: "head",
			id: markerHeadID,
			lineProps,
			markerHeight: markerDimension,
			markerWidth: markerDimension,
			refX: refX || 11,
			refY,
		};
		const tailMarkerProperties = {
			...headMarkerProperties,
			key: "tail",
			id: markerTailID,
			refX: refX || 13,
		};

		// head arrow
		switch (headEnd) {
			case LineCap.LINE_ARROW:
				markerObject.push(
					<Marker
						{...headMarkerProperties}
						fill="none"
						path="M 24 4 L 10 12 L 24 20"
					/>,
				);
			case LineCap.STEALTH_ARROW:
				markerObject.push((
					<Marker
						{...headMarkerProperties}
						path="M 24 4 L 6 12 L 24 20 Q 16 12 24 4"
					/>
				));
			case LineCap.TRIANGLE_ARROW:
				markerObject.push(
					<Marker
						{...headMarkerProperties}
						path="M 24 4 L 6 12 L 24 20 z"
					/>,
				);
				break;
			case LineCap.NO_ARROW:
				markerObject.push(null);
				break;
			default:
				break;
		}
		// tail arrow
		switch (tailEnd) {
			case LineCap.LINE_ARROW:
				markerObject.push(
					<Marker
						{...tailMarkerProperties}
						fill="none"
						path="M 0 4 L 14 12 L 0 20"
					/>,
				);
			case LineCap.STEALTH_ARROW:
				markerObject.push(
					<Marker
						{...tailMarkerProperties}
						path="M 0 4 L 18 12 L 0 20 Q 8 12 0 4"
					/>,
				);
				break;
			case LineCap.TRIANGLE_ARROW:
				markerObject.push(
					<Marker
						{...tailMarkerProperties}
						path="M 0 4 L 18 12 L 0 20 z"
					/>,
				);
				break;
			case LineCap.NO_ARROW:
				markerObject.push(null);
				break;
			default:
				break;
		}

		return (
			<defs>
				{markerObject}
			</defs>
		);
	}
}

export default MarkerObject;
